/**
 * Wave 29 Chunk 10m — Stock-movements + manual stock-adjust panel.
 *
 * Drop-in modal that shows the full audit trail of stock movements for
 * one inventory item, plus a manual adjustment form (set new qty / apply
 * delta with a mandatory reason) that records a `source=manual` row.
 *
 * Usage:
 *   <StockMovementsModal item={inventoryRow} onClose={() => …} onSaved={refresh} />
 */
import { useEffect, useState } from "react";
import { History, Plus, Minus, Loader2, AlertTriangle, X, Save } from "lucide-react";
import Modal from "./Modal";
import api, { formatErr } from "../lib/api";
import { toast } from "./ui/sonner";

export default function StockMovementsModal({ item, onClose, onSaved }) {
  const [moves, setMoves] = useState([]);
  const [audit, setAudit] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("movements"); // movements | audit | adjust
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [mode, setMode] = useState("set");  // set | delta
  const [newQty, setNewQty] = useState(String(item.on_hand_qty ?? 0));
  const [delta, setDelta] = useState("");
  const [reason, setReason] = useState("");

  const reload = async () => {
    setLoading(true);
    try {
      const [m, a] = await Promise.all([
        api.get(`/inventory/${item.id}/stock-movements?limit=100`).then((r) => r.data).catch(() => []),
        api.get(`/inventory/${item.id}/price-audit?limit=50`).then((r) => r.data).catch(() => []),
      ]);
      setMoves(Array.isArray(m) ? m : []);
      setAudit(Array.isArray(a) ? a : []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); }, [item.id]);

  const submitAdjust = async (e) => {
    e.preventDefault();
    setErr("");
    if (!reason.trim()) return setErr("Reason is required for the audit log.");
    const body = { reason: reason.trim() };
    if (mode === "set") {
      const n = Number(newQty);
      if (!Number.isFinite(n)) return setErr("New quantity must be a number.");
      body.new_qty = n;
    } else {
      const d = Number(delta);
      if (!Number.isFinite(d) || d === 0) return setErr("Delta must be a non-zero number.");
      body.delta = d;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/inventory/${item.id}/stock-adjust`, body);
      toast.success(`Stock updated to ${data.on_hand_qty}.`);
      setReason("");
      setDelta("");
      setNewQty(String(data.on_hand_qty));
      await reload();
      setTab("movements");
      onSaved?.();
    } catch (e2) {
      setErr(formatErr(e2?.response?.data?.detail) || "Adjust failed.");
    } finally { setBusy(false); }
  };

  return (
    <Modal open onClose={onClose} title={null} maxWidth="max-w-2xl" testId="stock-movements-modal">
      <div className="space-y-3" data-testid="stock-movements-panel">
        <header className="flex items-center justify-between gap-2 flex-wrap">
          <h2 className="text-sm font-semibold flex items-center gap-2">
            <History className="w-4 h-4 text-[#1FB6C1]" />
            Stock history · <span className="text-[#9aa3b5] font-mono">{item.sku || "—"}</span>
          </h2>
          <span className="text-[10px] text-[#647087]">{item.name}</span>
        </header>

        <nav className="flex gap-1 border-b border-white/10">
          <Tab active={tab === "movements"} onClick={() => setTab("movements")} testId="stock-tab-movements">
            Movements <span className="text-[10px] text-[#647087]">({moves.length})</span>
          </Tab>
          <Tab active={tab === "audit"} onClick={() => setTab("audit")} testId="stock-tab-audit">
            Price audit <span className="text-[10px] text-[#647087]">({audit.length})</span>
          </Tab>
          <Tab active={tab === "adjust"} onClick={() => setTab("adjust")} testId="stock-tab-adjust">
            Adjust stock
          </Tab>
        </nav>

        {loading && <div className="p-6 text-center text-[10px] text-[#647087]"><Loader2 className="w-4 h-4 inline animate-spin" /> loading…</div>}

        {!loading && tab === "movements" && (
          <div className="max-h-[50vh] overflow-y-auto" data-testid="stock-movements-list">
            {moves.length === 0 ? (
              <div className="p-6 text-center text-[#647087] text-xs">No stock movements yet for this item.</div>
            ) : (
              <table className="w-full text-[11px]">
                <thead className="text-[10px] uppercase tracking-wider text-[#647087] border-b border-white/10">
                  <tr>
                    <th className="text-left py-1.5 px-1">When</th>
                    <th className="text-left py-1.5 px-1">Source</th>
                    <th className="text-right py-1.5 px-1">Δ</th>
                    <th className="text-right py-1.5 px-1">Before</th>
                    <th className="text-right py-1.5 px-1">After</th>
                    <th className="text-left py-1.5 px-1">Reason</th>
                    <th className="text-left py-1.5 px-1">By</th>
                  </tr>
                </thead>
                <tbody>
                  {moves.map((m) => {
                    const isReversal = m.source === "sales_doc_reversal";
                    return (
                      <tr key={m.id} className={`border-b border-white/5 ${isReversal ? "opacity-70" : ""}`} data-testid={`stock-row-${m.id}`}>
                        <td className="py-1.5 px-1 text-[#9aa3b5] font-mono text-[10px]">{(m.created_at || "").replace("T", " ").slice(0, 16)}</td>
                        <td className="py-1.5 px-1 text-[#dde3ec]">{m.source}{m.doc_number && <span className="text-[10px] text-[#647087]"> · {m.doc_number}</span>}</td>
                        <td className={`py-1.5 px-1 text-right font-mono ${m.qty_delta > 0 ? "text-emerald-300" : "text-rose-300"}`}>
                          {m.qty_delta > 0 ? "+" : ""}{m.qty_delta}
                        </td>
                        <td className="py-1.5 px-1 text-right font-mono text-[#9aa3b5]">{m.balance_before}</td>
                        <td className="py-1.5 px-1 text-right font-mono text-white">{m.balance_after}</td>
                        <td className="py-1.5 px-1 text-[#9aa3b5] truncate max-w-[180px]" title={m.reason}>{m.reason || "—"}</td>
                        <td className="py-1.5 px-1 text-[#9aa3b5]">{m.created_by_name || "—"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        )}

        {!loading && tab === "audit" && (
          <div className="max-h-[50vh] overflow-y-auto" data-testid="price-audit-list">
            {audit.length === 0 ? (
              <div className="p-6 text-center text-[#647087] text-xs">No master-price overrides recorded.</div>
            ) : (
              <table className="w-full text-[11px]">
                <thead className="text-[10px] uppercase tracking-wider text-[#647087] border-b border-white/10">
                  <tr>
                    <th className="text-left py-1.5 px-1">When</th>
                    <th className="text-right py-1.5 px-1">Old</th>
                    <th className="text-right py-1.5 px-1">New</th>
                    <th className="text-right py-1.5 px-1">Δ%</th>
                    <th className="text-left py-1.5 px-1">Reason</th>
                    <th className="text-left py-1.5 px-1">By</th>
                  </tr>
                </thead>
                <tbody>
                  {audit.map((a) => {
                    const pct = a.old_price ? ((a.new_price - a.old_price) / a.old_price) * 100 : null;
                    return (
                      <tr key={a.id} className="border-b border-white/5" data-testid={`price-audit-row-${a.id}`}>
                        <td className="py-1.5 px-1 text-[#9aa3b5] font-mono text-[10px]">{(a.changed_at || "").replace("T", " ").slice(0, 16)}</td>
                        <td className="py-1.5 px-1 text-right font-mono text-[#9aa3b5]">R {Number(a.old_price || 0).toFixed(2)}</td>
                        <td className="py-1.5 px-1 text-right font-mono text-white">R {Number(a.new_price || 0).toFixed(2)}</td>
                        <td className={`py-1.5 px-1 text-right font-mono ${pct != null && pct > 0 ? "text-emerald-300" : pct != null && pct < 0 ? "text-rose-300" : "text-[#647087]"}`}>
                          {pct != null ? `${pct > 0 ? "+" : ""}${pct.toFixed(1)}%` : "—"}
                        </td>
                        <td className="py-1.5 px-1 text-[#9aa3b5] truncate max-w-[180px]" title={a.reason}>{a.reason || "—"}</td>
                        <td className="py-1.5 px-1 text-[#9aa3b5]">{a.changed_by_name || "—"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        )}

        {!loading && tab === "adjust" && (
          <form onSubmit={submitAdjust} className="space-y-3 p-1" data-testid="stock-adjust-form">
            <div className="text-[11px] text-[#9aa3b5]">
              Current on hand: <span className="font-mono text-white">{item.on_hand_qty ?? 0}</span>
            </div>
            <div className="flex gap-1 border border-white/10 rounded p-0.5 w-fit">
              <ModeChip active={mode === "set"} onClick={() => setMode("set")} testId="stock-mode-set">Set new value</ModeChip>
              <ModeChip active={mode === "delta"} onClick={() => setMode("delta")} testId="stock-mode-delta">Apply delta</ModeChip>
            </div>
            {mode === "set" ? (
              <label className="block">
                <div className="text-[10px] uppercase tracking-wider text-[#647087] mb-1">New quantity</div>
                <input type="number" step="0.01" value={newQty} onChange={(e) => setNewQty(e.target.value)}
                  className="hv-input text-sm font-mono w-32" data-testid="stock-adjust-new-qty" />
              </label>
            ) : (
              <label className="block">
                <div className="text-[10px] uppercase tracking-wider text-[#647087] mb-1">Delta (positive adds, negative subtracts)</div>
                <input type="number" step="0.01" value={delta} onChange={(e) => setDelta(e.target.value)}
                  className="hv-input text-sm font-mono w-32" data-testid="stock-adjust-delta" />
              </label>
            )}
            <label className="block">
              <div className="text-[10px] uppercase tracking-wider text-[#647087] mb-1">Reason *</div>
              <input type="text" value={reason} onChange={(e) => setReason(e.target.value)}
                placeholder="e.g. physical count, shrinkage, returned to warehouse"
                className="hv-input text-sm w-full" data-testid="stock-adjust-reason" />
            </label>
            {err && <div className="text-[11px] text-rose-300 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> {err}</div>}
            <div className="flex items-center justify-end gap-2">
              <button type="button" onClick={() => setTab("movements")} className="hv-btn hv-btn-ghost text-xs" data-testid="stock-adjust-cancel">Cancel</button>
              <button type="submit" disabled={busy} className="hv-btn hv-btn-primary text-xs disabled:opacity-50" data-testid="stock-adjust-submit">
                {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                Apply
              </button>
            </div>
          </form>
        )}

        <div className="flex items-center justify-end pt-2 border-t border-white/5">
          <button type="button" onClick={onClose} className="hv-btn hv-btn-ghost text-xs" data-testid="stock-movements-close">
            <X className="w-3 h-3" /> Close
          </button>
        </div>
      </div>
    </Modal>
  );
}

function Tab({ active, onClick, testId, children }) {
  return (
    <button type="button" onClick={onClick} data-testid={testId}
      className={`px-3 py-1.5 text-xs border-b-2 transition ${active ? "border-[#1FB6C1] text-white" : "border-transparent text-[#a0aabf] hover:text-white"}`}>
      {children}
    </button>
  );
}

function ModeChip({ active, onClick, testId, children }) {
  return (
    <button type="button" onClick={onClick} data-testid={testId}
      className={`px-2 py-1 text-[10px] rounded ${active ? "bg-[#1FB6C1]/15 text-[#1FB6C1]" : "text-[#a0aabf] hover:bg-white/5"}`}>
      {children}
    </button>
  );
}
